/**
 * Pagina principal do Dashboard Publico do LegisCity (Home / Mapa).
 *
 * Layout modular em 3 zonas integradas ao redor do mapa interativo:
 * - Esquerda: Painel expansivel de exploracao (Busca, Filtros, Bairros e Lista de Indicacoes)
 * - Centro: Mapa de tela cheia com ferramentas cartograficas flutuantes
 * - Direita: Painel contextual de resumo estatistico
 * - Mobile: Barra de abas inferior para alternancia tatil entre Mapa, Indicacoes e Resumo
 *
 * @module pages/PublicDashboard
 */

import { useEffect, useState } from "react";
import MapView from "../components/MapView";
import IndicacoesPanel from "../components/IndicacoesPanel";
import StatsPanel from "../components/StatsPanel";
import MobileBottomNav from "../components/MobileBottomNav";
import { useSelectedIndicacao, useIndicacoes } from "../hooks/useIndicacoes";
import { useFilters } from "../hooks/useFilters";
import { getCategoryStyle } from "../constants/categories";

function useViewportIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 900);

  useEffect(() => {
    let timeoutId = null;
    const onResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 900);
      }, 100);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return isMobile;
}

export default function PublicDashboard({
  navigate,
  themeMode: externalThemeMode,
  toggleTheme: externalToggleTheme,
}) {
  const isMobile = useViewportIsMobile();
  const { selected, select } = useSelectedIndicacao();
  const { total: totalIndicacoes } = useIndicacoes();
  const { filters, applyFilters, setAutor } = useFilters();

  // Estados de tema (usa props globais ou fallback local)
  const [localThemeMode, setLocalThemeMode] = useState(() => {
    return document.documentElement.getAttribute("data-theme") || "light";
  });

  const themeMode = externalThemeMode || localThemeMode;

  const toggleTheme = () => {
    if (externalToggleTheme) {
      externalToggleTheme();
    } else {
      const nextTheme = themeMode === "light" ? "dark" : "light";
      setLocalThemeMode(nextTheme);
      document.documentElement.setAttribute("data-theme", nextTheme);
      localStorage.setItem("sapl-map-theme", nextTheme);
    }
  };

  // Estados de expansao/recolhimento dos paineis
  const [indicacoesCollapsed, setIndicacoesCollapsed] = useState(false);
  const [statsPanelCollapsed, setStatsPanelCollapsed] = useState(true);

  // Camadas ativas (exclusão mútua entre Marcadores, Mapa de Calor e Coroplético por Bairro)
  const [isHeatmapActive, setIsHeatmapActive] = useState(false);
  const [isChoroplethActive, setIsChoroplethActive] = useState(false);

  // ── Deep Linking: Carrega filtros e indicação pela URL na montagem ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const indParam = params.get("indicacao") || params.get("ind");
    const autorParam = params.get("autor");
    const anosParam = params.get("anos");
    const categoriaParam = params.get("categoria") || params.get("thematic_category");
    const bairroParam = params.get("bairro");

    const parsedIndId = indParam ? parseInt(indParam, 10) : null;
    const hasValidInd = Boolean(parsedIndId && !isNaN(parsedIndId));

    const initialFilters = {};
    if (autorParam) {
      initialFilters.autor = autorParam;
    }
    if (bairroParam) {
      initialFilters.bairro = bairroParam;
    }
    if (anosParam) {
      const parsedAnos = anosParam
        .split(",")
        .map((a) => parseInt(a.trim(), 10))
        .filter((a) => !isNaN(a));
      if (parsedAnos.length > 0) {
        initialFilters.anos = parsedAnos;
      }
    }
    if (categoriaParam) {
      const catStyle = getCategoryStyle(categoriaParam);
      if (catStyle && catStyle.key) {
        initialFilters.thematic_category = catStyle.key;
      }
    }

    const hasFiltersToApply = Object.keys(initialFilters).length > 0;

    if (hasFiltersToApply) {
      applyFilters(initialFilters, hasValidInd);
      if (initialFilters.autor || initialFilters.thematic_category) {
        setStatsPanelCollapsed(false);
      }
    }

    if (hasValidInd) {
      select(parsedIndId);
      if (isMobile) {
        setIndicacoesCollapsed(false);
        setStatsPanelCollapsed(true);
      } else {
        setIndicacoesCollapsed(false);
      }
    }
  }, [applyFilters, select, isMobile]);

  // ── Sincronização Atômica da URL com o Estado de Exploração ──
  useEffect(() => {
    const url = new URL(window.location.href);
    let changed = false;

    // 1. Indicação selecionada (?indicacao=)
    if (selected?.id) {
      const currentVal = url.searchParams.get("indicacao") || url.searchParams.get("ind");
      if (currentVal !== String(selected.id)) {
        url.searchParams.set("indicacao", String(selected.id));
        url.searchParams.delete("ind");
        changed = true;
      }
    } else {
      if (url.searchParams.has("indicacao") || url.searchParams.has("ind")) {
        url.searchParams.delete("indicacao");
        url.searchParams.delete("ind");
        changed = true;
      }
    }

    // 2. Filtro por Vereador (?autor=)
    if (filters.autor) {
      if (url.searchParams.get("autor") !== filters.autor) {
        url.searchParams.set("autor", filters.autor);
        changed = true;
      }
    } else {
      if (url.searchParams.has("autor")) {
        url.searchParams.delete("autor");
        changed = true;
      }
    }

    // 3. Filtro por Anos (?anos=)
    if (url.searchParams.has("ano")) {
      url.searchParams.delete("ano");
      changed = true;
    }
    if (Array.isArray(filters.anos) && filters.anos.length > 0) {
      const anosStr = filters.anos.join(",");
      if (url.searchParams.get("anos") !== anosStr) {
        url.searchParams.set("anos", anosStr);
        changed = true;
      }
    } else {
      if (url.searchParams.has("anos")) {
        url.searchParams.delete("anos");
        changed = true;
      }
    }

    // 4. Filtro por Categoria Temática (?categoria=)
    if (filters.thematic_category) {
      if (url.searchParams.get("categoria") !== filters.thematic_category) {
        url.searchParams.set("categoria", filters.thematic_category);
        url.searchParams.delete("thematic_category");
        changed = true;
      }
    } else {
      if (url.searchParams.has("categoria") || url.searchParams.has("thematic_category")) {
        url.searchParams.delete("categoria");
        url.searchParams.delete("thematic_category");
        changed = true;
      }
    }

    // 5. Filtro por Bairro (?bairro=)
    if (filters.bairro) {
      if (url.searchParams.get("bairro") !== filters.bairro) {
        url.searchParams.set("bairro", filters.bairro);
        changed = true;
      }
    } else {
      if (url.searchParams.has("bairro")) {
        url.searchParams.delete("bairro");
        changed = true;
      }
    }

    if (changed) {
      window.history.replaceState({}, "", url.toString());
    }

    // Gerencia expansão dos painéis ao selecionar indicação
    if (selected?.id) {
      if (isMobile) {
        setIndicacoesCollapsed(false);
        setStatsPanelCollapsed(true);
      } else {
        setIndicacoesCollapsed(false);
      }
    }
  }, [
    selected?.id,
    filters.autor,
    filters.ano,
    filters.anos,
    filters.thematic_category,
    filters.bairro,
    isMobile,
  ]);

  // Abre o painel contextual ao ativar filtro de vereador, categoria ou bairro
  useEffect(() => {
    if (filters.autor || filters.thematic_category || filters.bairro) {
      setStatsPanelCollapsed(false);
    }
  }, [filters.autor, filters.thematic_category, filters.bairro]);

  const handleSelectLayer = (layerMode) => {
    if (layerMode === "heatmap") {
      setIsHeatmapActive(true);
      setIsChoroplethActive(false);
    } else if (layerMode === "choropleth") {
      setIsChoroplethActive(true);
      setIsHeatmapActive(false);
    } else {
      setIsHeatmapActive(false);
      setIsChoroplethActive(false);
    }
  };

  const handleToggleHeatmap = () => {
    setIsHeatmapActive((prev) => {
      const next = !prev;
      if (next) setIsChoroplethActive(false);
      return next;
    });
  };

  const handleToggleChoropleth = () => {
    setIsChoroplethActive((prev) => {
      const next = !prev;
      if (next) setIsHeatmapActive(false);
      return next;
    });
  };

  const handleToggleIndicacoes = () => {
    const next = !indicacoesCollapsed;
    setIndicacoesCollapsed(next);
    if (!next && isMobile) {
      setStatsPanelCollapsed(true);
    }
  };

  const handleToggleStats = () => {
    const next = !statsPanelCollapsed;
    setStatsPanelCollapsed(next);
    if (!next && isMobile) {
      setIndicacoesCollapsed(true);
    }
  };

  // ── Controle de Abas Mobile ──
  const activeMobileTab = !indicacoesCollapsed
    ? "indicacoes"
    : !statsPanelCollapsed
    ? "resumo"
    : "mapa";

  const handleSelectMobileTab = (tab) => {
    if (tab === "mapa") {
      setIndicacoesCollapsed(true);
      setStatsPanelCollapsed(true);
    } else if (tab === "indicacoes") {
      setIndicacoesCollapsed(false);
      setStatsPanelCollapsed(true);
    } else if (tab === "resumo") {
      setIndicacoesCollapsed(true);
      setStatsPanelCollapsed(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "calc(100vh - 54px)",
        overflow: "hidden",
        background: "var(--bg-app)",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* ── 1. Mapa Interativo (Ocupa 100% da tela util) ── */}
      <div style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
        <MapView
          isDarkMode={themeMode === "dark"}
          activeLayers={{ heatmap: isHeatmapActive, choropleth: isChoroplethActive }}
          onSelectLayer={handleSelectLayer}
          onToggleHeatmap={handleToggleHeatmap}
          onToggleChoropleth={handleToggleChoropleth}
          statsPanelCollapsed={statsPanelCollapsed}
          isMobile={isMobile}
        />
      </div>

      {/* ── 2. Painel Mestre de Indicacoes (Esquerda) ── */}
      <IndicacoesPanel
        isMobile={isMobile}
        isCollapsed={indicacoesCollapsed}
        onToggleCollapse={handleToggleIndicacoes}
        topOffset={14}
      />

      {/* ── 3. Painel de Estatisticas Interativo (Direita) ── */}
      <StatsPanel
        isCollapsed={statsPanelCollapsed}
        onToggleCollapse={handleToggleStats}
        isMobile={isMobile}
        topOffset={14}
        navigate={navigate}
      />

      {/* ── 5. Barra de Navegacao Inferior Tatil (Exclusivo Mobile) ── */}
      {isMobile && (
        <MobileBottomNav
          activeTab={activeMobileTab}
          onSelectTab={handleSelectMobileTab}
          indicacoesCount={totalIndicacoes}
          hasSelected={Boolean(selected?.id)}
          autorFilter={filters?.autor}
        />
      )}
    </div>
  );
}
